"use client";

import React from "react";
import { Controller, useWatch } from "react-hook-form";
import {
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { handleEnterKey } from "../../utils/utils";

export default function Step1({ register, control, next }) {
  const values = useWatch({ control, defaultValue: {} });

  const mobileDigits = values.primaryContact?.replace(/\D/g, "") || "";
  const isMobileValid = mobileDigits.length === 10;

  const dobValid = values.dob ? new Date(values.dob) < new Date() : false;

  const isNextEnabled =
    values.email?.trim() &&
    values.firstName?.trim() &&
    values.lastName?.trim() &&
    values.city?.trim() &&
    values.state?.trim() &&
    values.participationType &&
    values.gender &&
    isMobileValid &&
    dobValid;

  return (
    <div className="max-w-[650px] mx-auto bg-white-1 rounded-lg shadow">
      <span className="block py-1 text-center text-base rounded-t-lg font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white-1">
        6th Tour De Sanawar 2025
      </span>
      <div className="w-full h-full p-6 pt-3">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-extrabold text-gray-800">
            Race Registration
          </h2>
          <p className="mt-2 text-gray-500 text-sm sm:text-base">
            Complete your details to secure your participation
          </p>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Email */}
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              {...register("email", { required: true })}
              placeholder="you@example.com"
              onKeyDown={handleEnterKey}
            />
            <TextField
              label="First Name"
              variant="outlined"
              fullWidth
              {...register("firstName", { required: true })}
              placeholder="John"
              onKeyDown={handleEnterKey}
            />
          </div>

          {/* First & Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label="Last Name"
              variant="outlined"
              fullWidth
              {...register("lastName", { required: true })}
              placeholder="Doe"
              onKeyDown={handleEnterKey}
            />
            {/* Mobile Number */}
            <TextField
              label="Primary Contact Number"
              variant="outlined"
              fullWidth
              {...register("primaryContact", {
                required: true,
                validate: (value) => {
                  const digitsOnly = value.replace(/\D/g, "");
                  if (!digitsOnly) return "Mobile number is required";
                  if (digitsOnly.length !== 10)
                    return "Mobile number must be exactly 10 digits";
                  return true;
                },
              })}
              placeholder="9876543210"
              inputProps={{
                maxLength: 10,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onInput={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                if (e.target.value !== digits) e.target.value = digits;
              }}
            />
          </div>

          <FormControl fullWidth>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Gender
            </label>
            <Controller
              name="gender"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select Gender</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              )}
            />
          </FormControl>

          {/* DOB, City, State */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TextField
              label="Date of Birth"
              type="date"
              variant="outlined"
              fullWidth
              {...register("dob", { required: true })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split("T")[0] }}
            />
            <TextField
              label="Resident City"
              variant="outlined"
              fullWidth
              {...register("city", { required: true })}
              placeholder="New Delhi"
            />
            <TextField
              label="Resident State"
              variant="outlined"
              fullWidth
              {...register("state", { required: true })}
              placeholder="Delhi"
            />
          </div>

          {/* Participant Type */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Participant Type</FormLabel>
            <Controller
              control={control}
              name="participationType"
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value="race"
                    control={<Radio />}
                    label="Race Participant"
                  />
                  <FormControlLabel
                    value="spectator"
                    control={<Radio />}
                    label="Spectator"
                  />
                </RadioGroup>
              )}
            />
            {values.participationType === "spectator" && (
              <p className="mt-2 text-sm text-yellow-600">
                As a Spectator, you will skip next steps and proceed directly to
                payment.
              </p>
            )}
          </FormControl>
        </div>

        {/* Next Button */}
        <div className="flex justify-end mt-6">
          <Button
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: "var(--color-blue)",
              "&:hover": { backgroundColor: "var(--color-blue-hover)" },
            }}
            onClick={next}
            disabled={!isNextEnabled}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
